import { useEffect, useCallback } from "react";

/**
 * useKeyboardShortcuts hook
 * Provides keyboard navigation for the Time Collection App
 *
 * Shortcuts:
 * - 1-9: Quick select jobs in list
 * - S: Start selected job
 * - P: Pause running job
 * - R: Resume paused job
 * - C: Complete job (opens modal)
 * - Escape: Close drawer/modal, clear selection
 * - Arrow Up/Down: Navigate jobs list
 * - Space: Toggle job selection (multi-select)
 * - ?: Show keyboard shortcuts help
 */
export function useKeyboardShortcuts({
  jobs = [],
  selectedJob,
  selectedJobIds = [],
  activeSessions = {},
  selectedOperation,
  onJobClick,
  onStartJob,
  onPauseJob,
  onResumeJob,
  onCompleteJob,
  onSelectionChange,
  onCloseDrawer,
  enabled = true,
}) {
  // Get the currently focused job index
  const getFocusedJobIndex = useCallback(() => {
    if (selectedJob) {
      return jobs.findIndex((j) => j.id === selectedJob.id);
    }
    if (selectedJobIds.length > 0) {
      return jobs.findIndex((j) => j.id === selectedJobIds[selectedJobIds.length - 1]);
    }
    return -1;
  }, [jobs, selectedJob, selectedJobIds]);

  // Get session state for a job
  const getJobSessionState = useCallback(
    (job) => {
      if (!job) return null;
      const session = activeSessions[job.id];
      return session?.state || null;
    },
    [activeSessions]
  );

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (event) => {
      if (!enabled) return;

      // Don't handle if focus is in an input, textarea, or select
      const target = event.target;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }

      const key = event.key;
      const isCtrlOrMeta = event.ctrlKey || event.metaKey;

      // Number keys 1-9: Quick select job
      if (/^[1-9]$/.test(key) && !isCtrlOrMeta) {
        const index = parseInt(key, 10) - 1;
        if (index < jobs.length) {
          const job = jobs[index];
          if (event.shiftKey) {
            // Shift + number: Toggle selection for multi-select
            const newSelection = selectedJobIds.includes(job.id)
              ? selectedJobIds.filter((id) => id !== job.id)
              : [...selectedJobIds, job.id];
            onSelectionChange?.(newSelection);
          } else {
            // Just number: Select job
            onJobClick?.(job);
          }
          event.preventDefault();
        }
        return;
      }

      // Arrow keys for navigation
      if (key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        const currentIndex = getFocusedJobIndex();
        let newIndex;

        if (key === "ArrowDown") {
          newIndex = currentIndex < jobs.length - 1 ? currentIndex + 1 : 0;
        } else {
          newIndex = currentIndex > 0 ? currentIndex - 1 : jobs.length - 1;
        }

        if (newIndex >= 0 && newIndex < jobs.length) {
          const newJob = jobs[newIndex];
          if (event.shiftKey) {
            // Shift + Arrow: Add to selection
            if (!selectedJobIds.includes(newJob.id)) {
              onSelectionChange?.([...selectedJobIds, newJob.id]);
            }
          } else {
            onJobClick?.(newJob);
          }
        }
        return;
      }

      // Enter or Space: Toggle current job selection
      if (key === "Enter" || key === " ") {
        if (selectedJob && key === " ") {
          event.preventDefault();
          // Toggle in multi-select
          const newSelection = selectedJobIds.includes(selectedJob.id)
            ? selectedJobIds.filter((id) => id !== selectedJob.id)
            : [...selectedJobIds, selectedJob.id];
          onSelectionChange?.(newSelection);
        }
        return;
      }

      // Escape: Close drawer, clear selection
      if (key === "Escape") {
        event.preventDefault();
        if (selectedJob) {
          onCloseDrawer?.();
        } else if (selectedJobIds.length > 0) {
          onSelectionChange?.([]);
        }
        return;
      }

      // S: Start job
      if (key === "s" || key === "S") {
        if (!selectedJob || !selectedOperation) return;
        const state = getJobSessionState(selectedJob);
        if (!state) {
          event.preventDefault();
          onStartJob?.(selectedJob);
        }
        return;
      }

      // P: Pause running job
      if (key === "p" || key === "P") {
        if (!selectedJob) return;
        const state = getJobSessionState(selectedJob);
        if (state === "running") {
          event.preventDefault();
          onPauseJob?.(selectedJob);
        }
        return;
      }

      // R: Resume paused job
      if (key === "r" || key === "R") {
        if (!selectedJob) return;
        const state = getJobSessionState(selectedJob);
        if (state === "paused") {
          event.preventDefault();
          onResumeJob?.(selectedJob);
        }
        return;
      }

      // C: Complete job
      if (key === "c" || key === "C") {
        if (!selectedJob) return;
        const state = getJobSessionState(selectedJob);
        if (state === "running" || state === "paused") {
          event.preventDefault();
          onCompleteJob?.(selectedJob);
        }
        return;
      }

      // A: Select all jobs (with Ctrl/Cmd)
      if ((key === "a" || key === "A") && isCtrlOrMeta) {
        event.preventDefault();
        const allIds = jobs.map((j) => j.id);
        onSelectionChange?.(allIds);
        return;
      }

      // D: Deselect all (with Ctrl/Cmd)
      if ((key === "d" || key === "D") && isCtrlOrMeta) {
        event.preventDefault();
        onSelectionChange?.([]);
        return;
      }
    },
    [
      enabled,
      jobs,
      selectedJob,
      selectedJobIds,
      selectedOperation,
      getFocusedJobIndex,
      getJobSessionState,
      onJobClick,
      onStartJob,
      onPauseJob,
      onResumeJob,
      onCompleteJob,
      onSelectionChange,
      onCloseDrawer,
    ]
  );

  // Attach keyboard listener
  useEffect(() => {
    if (enabled) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [enabled, handleKeyDown]);

  return {
    shortcuts: [
      { key: "1-9", description: "Quick select job" },
      { key: "↑/↓", description: "Navigate jobs" },
      { key: "Shift+↑/↓", description: "Multi-select jobs" },
      { key: "S", description: "Start selected job" },
      { key: "P", description: "Pause running job" },
      { key: "R", description: "Resume paused job" },
      { key: "C", description: "Complete job" },
      { key: "Esc", description: "Close drawer / Clear" },
      { key: "Ctrl+A", description: "Select all jobs" },
      { key: "Ctrl+D", description: "Deselect all" },
    ],
  };
}

export default useKeyboardShortcuts;
